/**
 * Decline rate by reason, per payment family.
 *
 * "Why payments were refused" ranks reasons by COUNT, which mostly says how
 * much of each family there is. This divides each reason by the attempts in
 * its own family — card checkout, card renewal, each BNPL provider — so a
 * Klarna buyer's chance of not finishing reads beside a card buyer's chance
 * of insufficient funds. Shading is per table, one hue, light to dark; the
 * count sits under every rate so a high rate on a handful of attempts is
 * visible as such. Data: `reasonRates` from /payments/declines.
 */

import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from './ui'

const TYPE_DOT = { processor: '#ef4444', customer: '#8b5cf6', unknown: '#9ca3af' }
// Below this many attempts a family's rates are shown muted: one decline in
// eight attempts reads as 12.5% and means very little.
const THIN = 30

const pct = rate => {
  if (rate == null) return '—'
  if (rate === 0) return '0%'
  const p = rate * 100
  return p < 0.1 ? '<0.1%' : `${p < 10 ? p.toFixed(1) : Math.round(p)}%`
}

export default function DeclineReasonRates({ data, formatNumber = n => n.toLocaleString() }) {
  const [family, setFamily] = useState('split')
  if (!data?.reasons?.length) return null

  const shown = data.groups.filter(g => (family === 'bnpl' ? g.key === 'bnpl' || g.bnpl : family === 'split' ? g.key !== 'bnpl' : true))
  const hasBnpl = data.groups.some(g => g.bnpl)
  const max = Math.max(...data.reasons.flatMap(r => shown.map(g => r.byGroup[g.key]?.rate || 0)), 0.0001)

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Decline rate by reason</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Each reason as a share of the attempts made that way, with the count under it. Columns are the payment
              family; a family with fewer than {THIN} attempts is greyed out.
            </p>
          </div>
          {hasBnpl && (
            <div className="flex gap-1">
              {[['split', 'By family'], ['bnpl', 'BNPL only']].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFamily(key)}
                  className={`h-8 rounded-md px-3 text-xs font-medium ${family === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="text-left font-medium p-2 min-w-[12rem]">Reason</th>
                {shown.map(g => (
                  <th key={g.key} className={`text-right font-medium p-2 whitespace-nowrap ${g.attempted < THIN ? 'opacity-50' : ''}`}>
                    <div className="text-foreground">{g.label}</div>
                    <div className="font-normal">{formatNumber(g.attempted)} attempts</div>
                  </th>
                ))}
              </tr>
              <tr className="border-t">
                <td className="p-2 text-xs font-medium">All reasons</td>
                {shown.map(g => (
                  <td key={g.key} className={`p-2 text-right tabular-nums font-semibold ${g.attempted < THIN ? 'opacity-50' : ''}`}>
                    {pct(g.declineRate)}
                    <div className="text-xs font-normal text-muted-foreground">{formatNumber(g.declined)}</div>
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.reasons
                .filter(r => shown.some(g => r.byGroup[g.key]?.count))
                .map(r => (
                  <tr key={r.key} className="border-t">
                    <td className="p-2">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: TYPE_DOT[r.declineType] || TYPE_DOT.unknown }} />
                        <span>{r.name}</span>
                        {r.key?.startsWith('authorizedotnet:') && <span className="font-mono text-xs text-muted-foreground">{r.code}</span>}
                      </span>
                    </td>
                    {shown.map(g => {
                      const cell = r.byGroup[g.key] || { count: 0, rate: null }
                      const alpha = cell.rate ? 0.06 + 0.34 * (cell.rate / max) : 0
                      return (
                        <td
                          key={g.key}
                          className={`p-2 text-right tabular-nums ${g.attempted < THIN ? 'opacity-50' : ''}`}
                          style={alpha ? { background: `rgba(239, 68, 68, ${alpha.toFixed(3)})` } : undefined}
                          title={`${r.name} · ${g.label}: ${cell.count} of ${g.attempted} attempts`}
                        >
                          {cell.count ? pct(cell.rate) : <span className="text-muted-foreground">–</span>}
                          {cell.count > 0 && <div className="text-xs text-muted-foreground">{formatNumber(cell.count)}</div>}
                        </td>
                      )
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Card includes wallets. A card renewal is a stored card charged on our schedule; BNPL is always a customer at checkout,
          so its "not completed" is a customer leaving the provider's page, not a refusal.
        </p>
      </CardContent>
    </Card>
  )
}
